import React from "react";

import {
  Box,
  Table,
  Button,
  Switch,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  FormControlLabel,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

import NotificationPreferenceItem from "./NotificationPreferenceItem";

import {
  ALERT_TYPE,
  ALERT_TYPE_COLOR,
  ALERT_NOTIFICATION_METHODS,
  ALERT_NOTIFICATION_TIME_PERIOD,
  ALERT_NOTIFICATION_PREFERENCES_TABLE_HEADER,
} from "static/constants";

const NotificationPreferenceTable = ({ formikProps, isEditPreference }) => {
  const { values, setFieldValue } = formikProps;

  const notificationMethods = Object.values(ALERT_NOTIFICATION_METHODS);

  const getConfigsByTimePeriod = (configs, timePeriod) =>
    configs.filter((config) => config.time_period === timePeriod);

  const canRemoveConfig = (configs, timePeriod) =>
    getConfigsByTimePeriod(configs, timePeriod).length > 1;

  const getAvailableMethodForTimePeriod = (configs, timePeriod) => {
    const configsByTimePeriod = getConfigsByTimePeriod(configs, timePeriod);

    const usedMethods = new Set(
      configsByTimePeriod.map((config) => config.notification_method)
    );

    return notificationMethods.find((method) => !usedMethods.has(method));
  };

  const handleAddNotifyConfig = (configIndex, timePeriod) => {
    const existingConfigs =
      values?.configurations?.[configIndex]?.notify_configs || [];
    const nextMethod = getAvailableMethodForTimePeriod(
      existingConfigs,
      timePeriod
    );

    if (!nextMethod) {
      return;
    }

    const newConfig = {
      active: false,
      time_period: timePeriod,
      notification_method: nextMethod,
      include_transmission_pdf: false,
      recipients: [],
      message_content: [],
    };

    setFieldValue(`configurations[${configIndex}].notify_configs`, [
      ...existingConfigs,
      newConfig,
    ]);
  };

  const handleRemoveNotifyConfig = (configIndex, notifyConfigIndex) => {
    const existingConfigs =
      values?.configurations?.[configIndex]?.notify_configs || [];
    const configToRemove = existingConfigs?.[notifyConfigIndex];

    if (!configToRemove) {
      return;
    }

    if (!canRemoveConfig(existingConfigs, configToRemove.time_period)) {
      return;
    }

    setFieldValue(
      `configurations[${configIndex}].notify_configs`,
      existingConfigs.filter((_, index) => index !== notifyConfigIndex)
    );
  };

  const renderNotificationItems = (configs, configIndex, timePeriod) => {
    const configsByTimePeriod = getConfigsByTimePeriod(configs, timePeriod);

    return configsByTimePeriod.map((notifyConfig, notifyConfigIndex) => (
      <NotificationPreferenceItem
        key={`${configIndex}-${timePeriod}-${notifyConfigIndex}`}
        formikProps={formikProps}
        configIndex={configIndex}
        notifyConfig={notifyConfig}
        notifyConfigIndex={configs.indexOf(notifyConfig)}
        timePeriodIndex={notifyConfigIndex}
        previousNotifyConfig={configsByTimePeriod[notifyConfigIndex - 1]}
        isEditPreference={isEditPreference}
        canRemoveConfig={canRemoveConfig(configs, timePeriod)}
        onRemoveConfig={handleRemoveNotifyConfig}
      />
    ));
  };

  const renderAddButton = (configs, configIndex, timePeriod) => {
    const availableMethod = getAvailableMethodForTimePeriod(
      configs,
      timePeriod
    );

    if (!availableMethod) {
      return null;
    }

    return (
      <Button
        variant="text"
        color="secondary"
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={() => handleAddNotifyConfig(configIndex, timePeriod)}
      >
        Add new configuration
      </Button>
    );
  };

  const handleCopyToAfterHours = (configIndex, checked) => {
    const notifyConfigs = values?.configurations?.[configIndex]?.notify_configs;
    const regularConfigs = notifyConfigs?.filter(
      (config) => config?.time_period === "regular"
    );

    const afterHoursConfigs = regularConfigs.map((regularConfig) => ({
      ...regularConfig,
      time_period: "after_hours",
      same_as_regular: checked,
    }));

    const updatedNotifyConfigs = [...regularConfigs, ...afterHoursConfigs];

    setFieldValue(
      `configurations[${configIndex}].notify_configs`,
      updatedNotifyConfigs
    );
  };

  const areAllAfterHoursSameAsRegular = (configs) => {
    const afterHoursConfigs = configs.filter(
      (config) => config.time_period === "after_hours"
    );
    return (
      afterHoursConfigs.length > 0 &&
      afterHoursConfigs.every((config) => config.same_as_regular === true)
    );
  };

  const handleCopyToYellowAlert = (configIndex, checked) => {
    const redAlertConfig = values.configurations?.find(
      (config) => config?.alert_type === "red"
    );

    if (redAlertConfig) {
      setFieldValue(`configurations[${configIndex}]`, {
        ...redAlertConfig,
        alert_type: "yellow",
        same_as_red: checked,
      });
    }
  };

  return (
    <div className="preferences-table">
      <Table>
        <TableHead>
          <TableRow>
            {ALERT_NOTIFICATION_PREFERENCES_TABLE_HEADER?.filter((header) =>
              isEditPreference ? true : header !== "Actions"
            )?.map((header) => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.configurations?.map((config, configIndex) => (
            <React.Fragment key={config.alert_type}>
              <TableRow
                className="column-header"
                sx={{
                  backgroundColor:
                    ALERT_TYPE_COLOR[config.alert_type] || "transparent",
                }}
              >
                <TableCell colSpan={12} sx={{ py: 1 }}>
                  <div
                    className="d-flex align-items-center"
                    style={{ minHeight: "40px" }}
                  >
                    <strong>{ALERT_TYPE[config.alert_type]}</strong>
                    {config.alert_type === "yellow" && isEditPreference && (
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            name="same_as_red"
                            color="secondary"
                            size="small"
                            defaultChecked={config.active || false}
                            onChange={(e, checked) =>
                              handleCopyToYellowAlert(configIndex, checked)
                            }
                          />
                        }
                        label="Use same preferences as Red Alert"
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>

              {/* Regular Hours Section */}
              <TableRow>
                <TableCell colSpan={12} className="column-header">
                  <div
                    className="d-flex align-items-center justify-between"
                    style={{ gap: 1, minHeight: "32px" }}
                  >
                    <span>{ALERT_NOTIFICATION_TIME_PERIOD["regular"]}</span>

                    {isEditPreference &&
                      renderAddButton(
                        config.notify_configs,
                        configIndex,
                        "regular"
                      )}
                  </div>
                </TableCell>
              </TableRow>

              {/* Regular Hours Configs */}
              {renderNotificationItems(
                config.notify_configs,
                configIndex,
                "regular"
              )}

              {/* After Hours Section */}
              <TableRow>
                <TableCell colSpan={12} className="column-header">
                  <Box
                    className="d-flex align-items-center justify-between"
                    sx={{ gap: 1, minHeight: "32px" }}
                  >
                    <Box className="d-flex align-items-center" sx={{ gap: 1 }}>
                      <span>
                        {ALERT_NOTIFICATION_TIME_PERIOD["after_hours"]}
                      </span>

                      {isEditPreference && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={areAllAfterHoursSameAsRegular(
                                config.notify_configs
                              )}
                              onChange={(e) =>
                                handleCopyToAfterHours(
                                  configIndex,
                                  e.target.checked
                                )
                              }
                              size="small"
                              color="secondary"
                            />
                          }
                          label="Use same preferences as Regular Hours"
                          labelPlacement="start"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Box>

                    {isEditPreference &&
                      renderAddButton(
                        config.notify_configs,
                        configIndex,
                        "after_hours"
                      )}
                  </Box>
                </TableCell>
              </TableRow>

              {/* After Hours Configs */}
              {renderNotificationItems(
                config.notify_configs,
                configIndex,
                "after_hours"
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default NotificationPreferenceTable;
